import { CSSProperties, FC, memo, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import { SCREENS } from 'src/constants/screens';
import { COLORS } from 'src/constants/styles';
import { appStore } from 'src/mobx/appStore';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { history } from 'src/services/history';
import { IBranch } from 'src/types/branches';
import { TBooleanInNumber } from 'src/types/main';
import { booleanToNumber, getTranslate } from 'src/utils/helpers';
import { confirmBranchBusy, confirmDelete } from 'src/utils/modalConfirmations';
import CustomTooltip, { TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import {
  getBranchOrderTypeIconNames,
  getBranchPaymentIconNames,
} from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/helpers/branches';
import branchesStore from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/store/branchesStore';
import { Icons } from '~/assets/fonts/Icons/icons';
import { modalStore } from '~/mobx/modalStore';
import Label from '~/view/components/Controls/Label';
import './style.scss';

interface IProps {
  branch?: IBranch;
  mainColumnWidth: number; // branch name
  secondaryColumnWidth: number; // other columns
}

const RowCell: FC<{ children: ReactNode; width: number; className?: string; onClick?: () => void }> = ({
  children,
  width,
  className = '',
  onClick,
}): JSX.Element => {
  return (
    <div className={`cell w-${width} ${onClick ? 'cursor-pointer' : ''} ${className}`} onClick={onClick}>
      {children}
    </div>
  );
};

const BranchListRow: FC<IProps> = ({ branch, mainColumnWidth, secondaryColumnWidth }: IProps): JSX.Element => {
  const onClickEdit = () => {
    if (!branch) return;
    history.push(SCREENS.BRANCHES_EDIT(branch.id));
  };

  const onChangeBranchAvailabilityStatus = (status: TBooleanInNumber, cb?: () => void) => {
    if (!branch) return;
    if (appStore.settings?.donation.enabled && appStore.settings?.donation.branch_id === branch.id) {
      modalStore.openErrorModal({ message: localeStore.t('donation_branch_pickup') });
      return;
    }
    branchesStore.updateBranch(branch.id, { status });
    cb?.();
  };

  const onChangeBusyStatus = async (status: TBooleanInNumber, cb?: () => void) => {
    if (!branch) return;

    const changeStatus = () => {
      branchesStore.updateBranch(branch.id, { is_busy: !!status });
      cb?.();
    };

    if (!status) {
      changeStatus();
      return;
    }

    const BranchBusyExpire = await branchesStore.supposedBusyExpire(branch.id);
    // show confirmation on enabling status
    confirmBranchBusy({
      message: BranchBusyExpire,
      onConfirm: changeStatus,
    });
  };

  const onPressDelete = () => {
    confirmDelete({
      title: localeStore.t('delete_branch_question'),
      message: localeStore.t('confirm_deleting_branch_text'),
      onConfirm: () => branch && branchesStore.deleteBranch(branch.id),
      redConfirmBtn: true,
    });
  };

  const onPressRestore = () => branch && branchesStore.restoreBranch(branch.id);

  const {
    is_pos: isPosEnabled,
    is_franchise_unintegrated_branches: isFranchiseBranchesEnabled,
    is_franchise: isFranchiseEnabled,
  } = appStore.settings || {};

  const orderTypeIcons = getBranchOrderTypeIconNames(branch);
  const paymentIcons = getBranchPaymentIconNames(branch?.payment_types);

  const isDisabledDeletion: boolean = !!(isPosEnabled && !branch?.is_delete && branch?.pos_service_id);

  const preorderStatusText = branch?.preorder ? 'status_on' : 'status_off';
  const preorderStatusColor = branch?.preorder ? COLORS.GREEN : COLORS.RED;

  return (
    <div className="branches-list-row">
      <RowCell width={branch?.is_delete ? 90 : mainColumnWidth} onClick={!branch?.is_delete ? onClickEdit : undefined}>
        {branch ? <p className="family-medium text-size-16 text-elipsis">{getTranslate(branch.name)}</p> : renderSkeleton(150)}
      </RowCell>
      {!branch?.is_delete && (
        <>
          <RowCell width={secondaryColumnWidth}>
            {branch ? (
              <>
                {!!branch.delivery_paused && (
                  <CustomTooltip
                    message={'delivery_paused_hint'}
                    tooltipClassName="bg-blur"
                    position={TOOLTIP_POSITION.FIXED}
                    show={!!branch.delivery_paused}
                    contentClassName="branches-list-row__icon"
                  >
                    <Icon
                      key={Icons.Delivery + 'disabled'}
                      name={Icons.Delivery}
                      onPress={onClickEdit}
                      className={`mr-3 ${!!branch.delivery_paused ? 'branches-list-row__icon_disabled' : 'branches-list-row__icon'}`}
                    />
                    <Icon key={Icons.Pause} name={Icons.Pause} className="branches-list-row__icon_overlay" />
                  </CustomTooltip>
                )}
                {orderTypeIcons.map((iconName) => (
                  <Icon key={iconName} onPress={onClickEdit} name={iconName} className="mr-3 branches-list-row__icon" />
                ))}
              </>
            ) : (
              renderSkeleton(75)
            )}
          </RowCell>
          <RowCell width={secondaryColumnWidth} onClick={onClickEdit}>
            {branch ? (
              <Label color={preorderStatusColor} text={localeStore.t(preorderStatusText)} className="text-size-12" />
            ) : (
              renderSkeleton(50, 24, { borderRadius: 12 })
            )}
          </RowCell>
          <RowCell width={secondaryColumnWidth} onClick={onClickEdit}>
            {branch
              ? paymentIcons.map((iconName) => <Icon key={iconName} name={iconName} className="mr-3 branches-list-row__icon" />)
              : renderSkeleton(75)}
          </RowCell>
          {isFranchiseBranchesEnabled && isPosEnabled && (
            <RowCell width={secondaryColumnWidth} onClick={onClickEdit}>
              {branch ? <p className="family-medium text-size-16">{branch.created_in}</p> : renderSkeleton(100)}
            </RowCell>
          )}
          {isFranchiseEnabled && (
            <RowCell width={secondaryColumnWidth} onClick={onClickEdit}>
              {branch ? <p className="family-medium text-elipsis text-size-16">{branch.franchise_profile}</p> : renderSkeleton(100)}
            </RowCell>
          )}
          <RowCell width={secondaryColumnWidth}>
            {branch ? (
              <Switcher
                status={booleanToNumber(branch.is_busy)}
                activeClassName="bg-red"
                hasCallback
                disabled={!branch.working_time.length || !branch.status}
                onChange={onChangeBusyStatus}
              />
            ) : (
              renderSkeleton(50, 30, { borderRadius: 30 })
            )}
          </RowCell>
        </>
      )}
      <RowCell width={secondaryColumnWidth} className={`justify-content-${branch?.is_delete ? 'end' : 'between'}`}>
        {branch
          ? !branch.is_delete && (
              <IntegrationManager setting={integrationsStore.posRestrictions?.branchAvailabilityChange} withTooltip>
                <CustomTooltip
                  message="select_branch_timeslot_first_errorenable_preorders_for_branch_toggle"
                  tooltipClassName="bg-blur"
                  messageClassName="text-size-14 color-red"
                  wrapperClassName="cursor-not-allowed"
                  position={TOOLTIP_POSITION.FIXED}
                  show={!branch.working_time.length}
                >
                  <Switcher status={branch.status} hasCallback disabled={!branch.working_time.length} onChange={onChangeBranchAvailabilityStatus} />
                </CustomTooltip>
              </IntegrationManager>
            )
          : renderSkeleton(50, 30, { borderRadius: 30 })}
        <CustomTooltip
          message="please_disable_posint"
          tooltipClassName="bg-blur"
          wrapperClassName="cursor-not-allowed"
          contentClassName="branches-list-row__disabled-control"
          position={TOOLTIP_POSITION.FIXED}
          show={isDisabledDeletion}
        >
          <DeleteRestoreBlock skeleton={!branch} isDelete={branch?.is_delete} onDelete={onPressDelete} onRestore={onPressRestore} />
        </CustomTooltip>
      </RowCell>
    </div>
  );
};

const renderSkeleton = (width: number = 100, height: number = 19.2, style?: CSSProperties) => {
  return <Skeleton width={width} height={height} style={style} />;
};

export default memo(BranchListRow);
